import { CheckCircle, Clock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getFirebaseAuth, getOrWaitForAuthToken } from '../../services/firebase'

type MarketStatus = 'open' | 'pre-open' | 'closed'

// Local Fallback Logic (Time-based)
function getLocalMarketStatus(): MarketStatus {
    const now = new Date()
    const hours = now.getHours()
    const minutes = now.getMinutes()
    const day = now.getDay()

    // Weekend check
    if (day === 0 || day === 6) return 'closed'

    const timeInMinutes = hours * 60 + minutes

    // Pre-open: 9:00 - 9:15
    if (timeInMinutes >= 540 && timeInMinutes < 555) return 'pre-open'

    // Market open: 9:15 - 15:30
    if (timeInMinutes >= 555 && timeInMinutes <= 930) return 'open'

    return 'closed'
}

export function MarketStatusIndicator() {
    const [localStatus, setLocalStatus] = useState<MarketStatus>(getLocalMarketStatus)
    const [liveMessage, setLiveMessage] = useState<string | null>(null)

    useEffect(() => {
        // 1. Update Local Status every minute
        const interval = setInterval(() => {
            setLocalStatus(getLocalMarketStatus())
        }, 60000)

        // 2. Fetch Live Status from our Proxy API
        const fetchLiveStatus = async () => {
            if (document.visibilityState === 'hidden') return // Save Cloudflare limits when backgrounded
            try {
                const token = await getOrWaitForAuthToken()
                if (!token) return // Avoid 401 error if user auth is initializing or not logged in
                const res = await fetch('/api/market-status', {
                    headers: { Authorization: `Bearer ${token}` }
                })
                if (res.ok) {
                    const data = await res.json()
                    if (data.status === 'success' && data.marketStatusMessage) {
                        setLiveMessage(data.marketStatusMessage)
                    }
                }
            } catch {
                // Ignore fallback error
            }
        }

        fetchLiveStatus()
        const liveInterval = setInterval(fetchLiveStatus, 60000)

        // Wake up immediately when tab becomes visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchLiveStatus()
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            clearInterval(interval)
            clearInterval(liveInterval)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    const msgLower = liveMessage ? liveMessage.toLowerCase() : ''
    
    // Default (Open)
    let textColor = 'text-slate-500 dark:text-slate-400 group-hover/status:text-emerald-700 dark:group-hover/status:text-emerald-400'
    let dotColor = 'bg-emerald-500 text-emerald-500'
    let pingColor = 'bg-emerald-400'
    let bgStyle = 'bg-transparent hover:bg-slate-200/60 dark:hover:bg-slate-800/60'
    let borderStyle = 'border-none'
    let text = localStatus === 'open' ? 'Market is Open' : localStatus === 'pre-open' ? 'Pre-Open Session' : 'Market has Closed'

    if (msgLower.includes('closed') || msgLower.includes('close') || (!liveMessage && localStatus === 'closed')) {
        textColor = 'text-slate-500 dark:text-slate-400 group-hover/status:text-rose-700 dark:group-hover/status:text-rose-400'
        dotColor = 'bg-rose-500 text-rose-500'
        pingColor = 'bg-rose-400'
        bgStyle = 'bg-transparent hover:bg-slate-200/60 dark:hover:bg-slate-800/60'
        borderStyle = 'border-none'
    } else if (msgLower.includes('pre') || (!liveMessage && localStatus === 'pre-open')) {
        textColor = 'text-slate-500 dark:text-slate-400 group-hover/status:text-amber-700 dark:group-hover/status:text-amber-400'
        dotColor = 'bg-amber-500 text-amber-500'
        pingColor = 'bg-amber-400'
        bgStyle = 'bg-transparent hover:bg-slate-200/60 dark:hover:bg-slate-800/60'
        borderStyle = 'border-none'
    }

    const formatStatus = (msg: string | null) => {
        if (!msg) return text
        const lower = msg.toLowerCase()
        if (lower.includes('normal market has closed')) return 'Market has Closed'
        if (lower.includes('normal market is open')) return 'Market is Open'
        if (lower.includes('post-close session')) return 'Post-Close Session'
        if (lower.includes('pre-open session closed')) return 'Pre-Open Session Closed'
        if (lower.includes('pre-open session')) return 'Pre-Open Session'
        return msg
    }

    const displayMsg = formatStatus(liveMessage)

    const [isOpen, setIsOpen] = useState(false)

    return (
        <div className="relative group/status">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                onBlur={() => setIsOpen(false)}
                className={`relative hidden sm:flex items-center justify-center gap-1.5 md:gap-2 px-3 py-1.5 md:px-3 md:py-1.5 lg:px-4 lg:py-2 rounded-full transition-colors duration-300 ${bgStyle} ${borderStyle} outline-none cursor-pointer`}
            >
                
                {/* Sonar Pinging Dot */}
                <div className="relative flex h-1.5 w-1.5 shrink-0 z-10 group-hover/status:scale-125 transition-transform duration-300 mx-1">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-40 ${pingColor}`}></span>
                    <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${dotColor} ring-[0.5px] ring-white/60 dark:ring-black/60`}></span>
                </div>
                
                <span 
                    className={`text-[13px] font-black transition-colors duration-300 ${textColor}`}
                    title={liveMessage ? `NSE: ${liveMessage}` : `NSE: ${text}`}
                >
                    NSE: {displayMsg}
                </span>
            </button>

            {/* Interactive Popup */}
            <div className={`absolute top-full right-0 mt-3 w-[260px] bg-white/90 dark:bg-slate-900/90 backdrop-blur-3xl border border-slate-200/80 dark:border-slate-700/80 rounded-[20px] shadow-[0_16px_40px_-12px_rgba(0,0,0,0.2)] dark:shadow-[0_16px_40px_-12px_rgba(0,0,0,0.6)] p-4 transition-all duration-300 origin-top-right z-50 ${isOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${dotColor.replace('text-', 'bg-').replace('bg-', 'bg-opacity-20 text-')}`}>
                        <Clock className="w-4 h-4" />
                    </div>
                    <div>
                        <h4 className="text-[13px] font-black text-slate-800 dark:text-slate-100 uppercase tracking-wide">Market Hours</h4>
                        <p className={`text-[10px] font-bold uppercase tracking-wider ${textColor}`}>{displayMsg}</p>
                    </div>
                </div>
                
                <div className="space-y-3">
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 dark:text-slate-400 font-bold">Pre-Open</span>
                        <span className="text-slate-800 dark:text-slate-200 font-mono font-black">09:00 - 09:15</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 dark:text-slate-400 font-bold">Normal</span>
                        <span className="text-slate-800 dark:text-slate-200 font-mono font-black">09:15 - 15:30</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 dark:text-slate-400 font-bold">Post-Close</span>
                        <span className="text-slate-800 dark:text-slate-200 font-mono font-black">15:40 - 16:00</span>
                    </div>
                </div>
            </div>
        </div>
    )
}
